import useSWR, { type SWRConfiguration } from 'swr';
import ConstituentQueryTypeFields from '@/constants/constituents/ConstituentQueryTypeFields';
import FilterParams from '@/constants/constituents/FilterParams';
import useMobileView from '@/hooks/ui/useMobileView';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import type AdvancedConstituentSearchParams from '@/types/AdvancedConstituentSearchParams';
import type SearchConstituent from '@/types/api/SearchConstituent';
import { createApiRequest } from '@/utils/services';
import laggy, { type WithLaggy } from '@/utils/swr/laggy';

const useAdvancedConstituentSearch = (
  params: Omit<AdvancedConstituentSearchParams, 'queryType'> = {
    filters: [],
  },
  swrOptions: SWRConfiguration = {},
) => {
  // Disregard column selection that might be in the QueryProvider on mobile
  const isMobile = useMobileView();
  const updatedParams = isMobile
    ? { ...params, columns: FilterParams.columns.defaultValue }
    : params;

  const ownerOrgId = useOwnerOrgId();
  const key = ownerOrgId
    ? [
        `orgs/${ownerOrgId}/constituents/search`,
        {
          ...updatedParams,
          queryType: ConstituentQueryTypeFields.V4,
        } as AdvancedConstituentSearchParams,
      ]
    : null;
  const swrResponse = useSWR(
    key,
    async ([path, data]) => {
      const response: APIResponse<{
        constituents: SearchConstituent[];
        searchParams: AdvancedConstituentSearchParams;
        totalHits: number;
      }> = await createApiRequest({ method: 'post', path, data });
      return response.data;
    },
    { use: [laggy], ...swrOptions },
  );
  return swrResponse as WithLaggy<typeof swrResponse>;
};

export default useAdvancedConstituentSearch;
